// Shared landmark/category layer for the real Jet Lag question system. Owns the
// category catalog (which support Matching vs Measuring), the "closest feature
// of a category to a point" computation (reused for the seeker's own answer AND
// per-station elimination), and the Matching/Measuring elimination logic.
//
// Matching = "is your nearest X the same as mine?" (identity / district
// containment). Measuring = "are you closer to or further from an X?" (distance
// comparison). Kept pure (no DOM/map) so the Ask UI and the engine share it.
import * as turf from "@turf/turf";
import { STATIONS, ZONE_RADIUS_MILES } from "./stations";
import { POIS, parkFC } from "./pois";
import { DIVISIONS } from "./divisions";
import coastlineData from "../public/data/coastline.json";

export type LL = [number, number];
type Kind = "points" | "parks" | "coastline" | "division";

export interface QCategory {
  id: string; label: string; emoji: string; color: string;
  kind: Kind; divId?: string;
  matching: boolean;  // supports "is your nearest X the same as mine?"
  measuring: boolean; // supports "closer to / further from an X?"
}

// Area airports (commercial) — small fixed set; avoids importing map.ts (maplibre).
const AIRPORTS = [
  { name: "JFK", lat: 40.6413, lng: -73.7781 },
  { name: "LaGuardia (LGA)", lat: 40.7769, lng: -73.8740 },
  { name: "Newark (EWR)", lat: 40.6895, lng: -74.1745 },
];

// Categories with data confirmed in-repo. (subway line / street / rail station /
// body-of-water / sea-level are deferred — see the recap handoff.)
export const CATEGORIES: QCategory[] = [
  { id: "airport", label: "Commercial airport", emoji: "✈️", color: "#2563eb", kind: "points", matching: true, measuring: true },
  { id: "museum", label: "Museum", emoji: "🏛", color: "#8e44ad", kind: "points", matching: true, measuring: true },
  { id: "library", label: "Library", emoji: "📚", color: "#2980b9", kind: "points", matching: true, measuring: true },
  { id: "cinema", label: "Movie theater", emoji: "🎬", color: "#c0392b", kind: "points", matching: true, measuring: true },
  { id: "hospital", label: "Hospital", emoji: "🏥", color: "#e74c3c", kind: "points", matching: true, measuring: true },
  { id: "golf", label: "Golf course", emoji: "⛳", color: "#1e8449", kind: "points", matching: true, measuring: true },
  { id: "zoo", label: "Zoo", emoji: "🦁", color: "#d35400", kind: "points", matching: true, measuring: true },
  { id: "aquarium", label: "Aquarium", emoji: "🐠", color: "#16a085", kind: "points", matching: true, measuring: true },
  { id: "amusement", label: "Amusement park", emoji: "🎢", color: "#e67e22", kind: "points", matching: true, measuring: true },
  { id: "consulate", label: "Foreign consulate", emoji: "🏳️", color: "#34495e", kind: "points", matching: true, measuring: true },
  { id: "mountain", label: "Mountain / peak", emoji: "⛰", color: "#7f8c8d", kind: "points", matching: true, measuring: true },
  { id: "park", label: "Park", emoji: "🌳", color: "#229954", kind: "parks", matching: true, measuring: true },
  { id: "coastline", label: "Coastline / water's edge", emoji: "🌊", color: "#0ea5e9", kind: "coastline", matching: false, measuring: true },
  // Administrative divisions: matching = which district you're IN; measuring = distance to nearest border.
  { id: "div-congressional", label: "Congressional district (1st)", emoji: "🏛️", color: "#9333ea", kind: "division", divId: "congressional", matching: true, measuring: true },
  { id: "div-communityboard", label: "Community board (2nd)", emoji: "🗳️", color: "#db2777", kind: "division", divId: "communityboard", matching: true, measuring: true },
  { id: "div-neighborhood", label: "Neighborhood (3rd)", emoji: "🏘️", color: "#0d9488", kind: "division", divId: "neighborhood", matching: true, measuring: true },
];
export const CAT_BY_ID: Record<string, QCategory> = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));
export const matchingCategories = () => CATEGORIES.filter((c) => c.matching);
export const measuringCategories = () => CATEGORIES.filter((c) => c.measuring);

const R = ZONE_RADIUS_MILES;
const coastline = coastlineData as unknown as GeoJSON.FeatureCollection;

function distMi(a: LL, b: LL): number {
  const Re = 3958.8, toR = Math.PI / 180;
  const dLat = (b[1] - a[1]) * toR, dLng = (b[0] - a[0]) * toR;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * toR) * Math.cos(b[1] * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * Re * Math.asin(Math.sqrt(x));
}

export interface Nearest { name: string; point: LL; dist: number; key: string }

// ---- cached geometry derivations (parks → edges, divisions → border lines) --
let parkCache: GeoJSON.Feature[] | null = null;
const parkPolys = () => (parkCache ??= parkFC().features);
const divLineCache: Record<string, GeoJSON.Feature[]> = {};
function divisionLines(divId: string): GeoJSON.Feature[] {
  if (divLineCache[divId]) return divLineCache[divId];
  const d = DIVISIONS.find((x) => x.id === divId);
  const out: GeoJSON.Feature[] = [];
  if (d) for (const f of d.data.features) {
    try {
      const l = turf.polygonToLine(f as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>);
      if (l.type === "FeatureCollection") out.push(...(l.features as GeoJSON.Feature[]));
      else out.push(l as GeoJSON.Feature);
    } catch { /* skip */ }
  }
  return (divLineCache[divId] = out);
}
function nearestOnLines(ref: LL, features: GeoJSON.Feature[], label: string): Nearest | null {
  const refPt = turf.point(ref);
  let best: Nearest | null = null;
  for (const f of features) {
    if (f.geometry?.type !== "LineString") continue;
    try {
      const np = turf.nearestPointOnLine(f as GeoJSON.Feature<GeoJSON.LineString>, refPt, { units: "miles" });
      const dst = np.properties.dist as number;
      if (!best || dst < best.dist) best = { name: label, point: np.geometry.coordinates as LL, dist: dst, key: label };
    } catch { /* skip */ }
  }
  return best;
}
function nearestPark(ref: LL): Nearest | null {
  const refPt = turf.point(ref);
  const cand = parkPolys()
    .map((f) => ({ f, cd: distMi(ref, turf.centroid(f).geometry.coordinates as LL) }))
    .filter((x) => x.cd < 14).sort((a, b) => a.cd - b.cd).slice(0, 40);
  let best: Nearest | null = null;
  for (const { f } of cand) {
    const l = turf.polygonToLine(f as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>);
    const lines = l.type === "FeatureCollection" ? (l.features as GeoJSON.Feature[]) : [l as GeoJSON.Feature];
    for (const ln of lines) {
      if (ln.geometry?.type !== "LineString") continue;
      try {
        const np = turf.nearestPointOnLine(ln as GeoJSON.Feature<GeoJSON.LineString>, refPt, { units: "miles" });
        const dst = np.properties.dist as number;
        const name = (f.properties?.name as string) || "Park";
        if (!best || dst < best.dist) best = { name, point: np.geometry.coordinates as LL, dist: dst, key: name };
      } catch { /* skip */ }
    }
  }
  return best;
}

/** The division feature that CONTAINS a point (for Matching divisions). */
export function divisionContaining(divId: string, lng: number, lat: number): { name: string } | null {
  const d = DIVISIONS.find((x) => x.id === divId);
  if (!d) return null;
  const pt = turf.point([lng, lat]);
  for (const f of d.data.features) {
    try { if (turf.booleanPointInPolygon(pt, f as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>)) return { name: (f.properties?.name as string) || "a district" }; }
    catch { /* skip */ }
  }
  return null;
}

/** Closest feature of a category to a point: name + distance (+ stable identity key). */
export function nearestOf(catId: string, lng: number, lat: number): Nearest | null {
  const cat = CAT_BY_ID[catId];
  if (!cat) return null;
  const ref: LL = [lng, lat];
  if (cat.kind === "points") {
    const pts = cat.id === "airport" ? AIRPORTS : (POIS[cat.id] || []);
    let best: Nearest | null = null;
    for (const p of pts) { const d = distMi(ref, [p.lng, p.lat]); if (!best || d < best.dist) best = { name: p.name || cat.label, point: [p.lng, p.lat], dist: d, key: `${p.lng.toFixed(5)},${p.lat.toFixed(5)}` }; }
    return best;
  }
  if (cat.kind === "parks") return nearestPark(ref);
  if (cat.kind === "coastline") return nearestOnLines(ref, coastline.features, "Water's edge");
  if (cat.kind === "division") {
    // For measuring we want the border distance; identity is handled separately.
    return nearestOnLines(ref, divisionLines(cat.divId!), "nearest border");
  }
  return null;
}

const active = (statusOf: (id: string) => "active" | "eliminated") => STATIONS.filter((s) => statusOf(s.id) === "active");

/** Matching: keep (yes) / drop (no) the stations sharing the seeker's nearest feature. */
export function matchingEliminate(catId: string, refLng: number, refLat: number, answer: "yes" | "no", statusOf: (id: string) => "active" | "eliminated"): string[] {
  const cat = CAT_BY_ID[catId];
  if (!cat) return [];
  const out: string[] = [];
  if (cat.kind === "division") {
    const mine = divisionContaining(cat.divId!, refLng, refLat)?.name ?? null;
    for (const s of active(statusOf)) {
      const same = (divisionContaining(cat.divId!, s.lng, s.lat)?.name ?? null) === mine;
      if (answer === "yes" ? !same : same) out.push(s.id);
    }
    return out;
  }
  const m0 = nearestOf(catId, refLng, refLat)?.key ?? null;
  if (!m0) return [];
  for (const s of active(statusOf)) {
    const same = (nearestOf(catId, s.lng, s.lat)?.key ?? null) === m0;
    if (answer === "yes" ? !same : same) out.push(s.id);
  }
  return out;
}

/** Measuring: drop stations whose nearest-category distance is farther (closer) / nearer (further). */
export function measuringEliminate(catId: string, seekerDist: number, answer: "closer" | "further", statusOf: (id: string) => "active" | "eliminated"): string[] {
  const out: string[] = [];
  for (const s of active(statusOf)) {
    const ds = nearestOf(catId, s.lng, s.lat)?.dist;
    if (ds == null) continue;
    // zone-aware: only eliminate when the whole ¼-mi zone is on the wrong side.
    const elim = answer === "closer" ? ds - R > seekerDist : ds + R < seekerDist;
    if (elim) out.push(s.id);
  }
  return out;
}

export function fmtDist(mi: number): string { return mi < 0.1 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(mi < 10 ? 2 : 1)} mi`; }
